// src/lib/firebase/services/productsClient.ts
"use client";

import {
  collection,
  doc,
  getDocs,
  getFirestore,
  orderBy,
  limit,
  where,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import type { Product } from "@/types/product";
import { initializeFirebase } from "@/firebase";

type NewProductData = Omit<Product, "id" | "createdAtUTC" | "updatedAtUTC">;

type CreateProductResult = {
  success: boolean;
  productId?: string;
  error?: string;
};

const serializeTimestamps = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }
  if (typeof data.toDate === "function") {
    return data.toDate().toISOString();
  }
  if (Array.isArray(data)) {
    return data.map((item) => serializeTimestamps(item));
  }
  if (typeof data === "object") {
    return Object.keys(data).reduce((acc: { [key: string]: any }, key) => {
      acc[key] = serializeTimestamps(data[key]);
      return acc;
    }, {});
  }
  return data;
};

export async function getProductsClient(): Promise<Product[]> {
  try {
    const { firebaseApp } = initializeFirebase();
    const db = getFirestore(firebaseApp);
    const productsCollection = collection(db, "products");
    const productsQuery = query(productsCollection, orderBy("Description", "asc"));
    const snapshot = await getDocs(productsQuery);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const serializedData = serializeTimestamps(data);
      return { ...serializedData, id: docSnap.id } as Product;
    });
  } catch (error) {
    console.error("[getProductsClient] Failed to fetch products:", error);
    return [];
  }
}

export async function getProductByCodeClient(productCode: string): Promise<Product | null> {
  try {
    const normalizedCode = String(productCode || "").trim();
    if (!normalizedCode) {
      return null;
    }

    const { firebaseApp } = initializeFirebase();
    const db = getFirestore(firebaseApp);
    const productsCollection = collection(db, "products");
    const productQuery = query(productsCollection, where("ProductCode", "==", normalizedCode), limit(1));
    const snapshot = await getDocs(productQuery);

    if (snapshot.empty) {
      return null;
    }

    const docSnap = snapshot.docs[0];
    const data = serializeTimestamps(docSnap.data());
    return { ...data, id: docSnap.id } as Product;
  } catch (error) {
    console.error("[getProductByCodeClient] Failed to fetch product:", error);
    return null;
  }
}

export async function createProductClient(productData: NewProductData): Promise<CreateProductResult> {
  try {
    const { firebaseApp } = initializeFirebase();
    const db = getFirestore(firebaseApp);
    const productsCollection = collection(db, "products");
    const docRef = doc(productsCollection);

    await setDoc(docRef, {
      ...productData,
      createdAtUTC: serverTimestamp(),
      updatedAtUTC: serverTimestamp(),
    });

    return { success: true, productId: docRef.id };
  } catch (error) {
    console.error("[createProductClient] Failed to add product:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to create product.",
    };
  }
}
